/**
 * Users Normalizer
 * Transforms users API responses to frontend models.
 * @module api/normalizers/users
 */

import { safeString, safeNumber, safeArray, safeObject } from "../safeHelpers"

// LLM:METADATA
// :hierarchy: [Domain:Users | Data_Normalization | Service]
// :relates-to: uses: "safeHelpers", required-by: "users.client.js"
// :rationale: "Transforms backend user responses to match frontend expectations (user structure, field naming, nested objects) for 9 user endpoints (list, details, CRUD, bulk operations, export, login-as)"
// :contract: pre: "validated data from users contract", post: "normalized user response for users client and Users.vue", invariant: "always returns expected structure"
// :decision_cache: "Chose comprehensive normalizer for users due to complex user object structure (contact info, permissions, timestamps) and multiple response formats across 9 endpoints [USERS-NORM-001]"
// LLM:END

/**
 * Normalize users list response
 * Transforms backend users list to frontend model structure
 *
 * @param {Object} response - Raw users list response from API
 * @param {Array} [response.users] - Array of user objects
 * @param {number} [response.total] - Total count of users
 * @returns {Object} Normalized users list
 * @returns {Array} returns.users - Normalized users array
 * @returns {number} returns.total - Total count
 */
export function normalizeUsersListResponse(response) {
  if (!response) {
    return {
      users: [],
      total: 0,
    }
  }

  const usersArray = Array.isArray(response.users)
    ? response.users.map(normalizeUserItem)
    : []

  return {
    users: usersArray,
    total: safeNumber(response.total, 0),
  }
}

/**
 * Normalize single user details response
 *
 * @param {Object} response - Raw user details response from API
 * @returns {Object} Normalized user details
 */
export function normalizeUserDetailsResponse(response) {
  if (!response) {
    return {
      user: {},
    }
  }

  return {
    user: normalizeUserDetails(response),
  }
}

/**
 * Normalize user item from list
 *
 * @param {Object} user - Raw user object
 * @returns {Object} Normalized user item
 */
function normalizeUserItem(user) {
  if (!user || typeof user !== "object") {
    return {}
  }

  return {
    userId: safeNumber(user.user_id),
    email: safeString(user.user_email || user.email),
    login: safeString(user.login),
    name: safeString(user.name),
    phone: safeString(user.phone),
    userBlocked: user.user_blocked === 1 || user.user_blocked === true,
    createdAt: safeString(user.created_at),
    selected: user.selected || false,
  }
}

/**
 * Normalize full user details
 *
 * @param {Object} user - Raw user details object
 * @returns {Object} Normalized user details
 */
function normalizeUserDetails(user) {
  if (!user || typeof user !== "object") {
    return {}
  }

  const userBlocked =
    user.user_blocked === 1 ||
    user.user_blocked === true ||
    user.isBlocked === 1 ||
    user.isBlocked === "1" ||
    user.isBlocked === true

  const hasActiveFlag =
    user.isActive !== undefined ||
    user.user_active !== undefined ||
    user.active !== undefined

  const isActive = hasActiveFlag
    ? user.isActive === "1" ||
      user.isActive === 1 ||
      user.isActive === true ||
      user.user_active === "1" ||
      user.user_active === 1 ||
      user.user_active === true ||
      user.active === "1" ||
      user.active === 1 ||
      user.active === true
    : !userBlocked

  return {
    userId: safeNumber(user.user_id || user.id),
    email: safeString(user.user_email || user.email),
    login: safeString(user.login),
    password: safeString(user.password), // Only for create/update
    name: safeString(user.name),
    phone: safeString(user.phone),
    address: safeString(user.address),
    city: safeString(user.city),
    postcode: safeString(user.postcode),
    country: safeString(user.country),
    userBlocked,
    isActive,
    role: safeString(user.role),
    agreeNewRules: user.agree_new_rules === 1 || user.agree_new_rules === true,
    favorites: safeArray(user.favorites),
    points: safeNumber(user.points, 0),
    cart: safeObject(user.cart),
    prevId: safeNumber(user.prev_id),
    prevLogin: safeString(user.prev_login),
    fbId: safeString(user.fb_id),
    vkId: safeString(user.vk_id),
    createdAt: safeString(user.created_at),
    updatedAt: safeString(user.updated_at),
  }
}

/**
 * Normalize create user response
 *
 * @param {Object} response - Raw create user response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {number} returns.userId - Created user ID
 * @returns {string} [returns.message] - Response message
 */
export function normalizeCreateUserResponse(response) {
  if (!response) {
    return {
      success: false,
      userId: 0,
      message: "No response from server",
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
    userId: safeNumber(response.user_id, 0),
    message: safeString(response.message),
  }
}

/**
 * Normalize update user response
 *
 * @param {Object} response - Raw update user response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.message] - Response message
 */
export function normalizeUpdateUserResponse(response) {
  if (!response) {
    return {
      success: false,
      message: "No response from server",
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
    message: safeString(response.message),
  }
}

/**
 * Normalize delete user response
 *
 * @param {Object} response - Raw delete user response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.message] - Response message
 */
export function normalizeDeleteUserResponse(response) {
  if (!response) {
    return {
      success: false,
      message: "No response from server",
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
    message: safeString(response.message),
  }
}

/**
 * Normalize bulk block/unblock response
 *
 * @param {Object} response - Raw bulk operation response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {number} returns.affectedCount - Number of affected users
 * @returns {string} [returns.message] - Response message
 */
export function normalizeBulkBlockResponse(response) {
  if (!response) {
    return {
      success: false,
      affectedCount: 0,
      message: "No response from server",
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
    affectedCount: safeNumber(response.affected_count, 0),
    message: safeString(response.message),
  }
}

/**
 * Normalize export users response
 *
 * @param {Object} response - Raw export response (can be blob or JSON)
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.downloadUrl] - Download URL for exported file
 * @returns {string} [returns.message] - Response message
 */
export function normalizeExportUsersResponse(response) {
  if (!response) {
    return {
      success: false,
      message: "No response from server",
    }
  }

  // Handle blob response (file download)
  if (response instanceof Blob) {
    return {
      success: true,
      blob: response,
    }
  }

  // Handle JSON response
  return {
    success: response.result === "ok" || response.result === "success",
    downloadUrl: safeString(response.download_url),
    message: safeString(response.message),
  }
}

/**
 * Normalize the backend login-as envelope to the SPA impersonation contract.
 *
 * @param {Object} response - Raw login-as payload validated by
 * LegacyLoginAsUserResponseSchema.
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - True when backend status is truthy (`1`,
 * `"1"`, `true`, `"ok"`, or `"success"`).
 * @returns {string} [returns.loginUrl] - Redirect target sourced from raw
 * `redirect_url`.
 * @returns {string} [returns.token] - Authentication token when backend sends
 * one.
 * @returns {string} [returns.message] - Backend message, or an empty string when
 * absent.
 * @mutates none
 * @sideeffects none
 */
export function normalizeLoginAsUserResponse(response) {
  if (!response) {
    return {
      success: false,
      message: "No response from server",
    }
  }

  const rawStatus = response.status
  const isSuccess =
    rawStatus === true ||
    rawStatus === 1 ||
    rawStatus === "1" ||
    rawStatus === "ok" ||
    rawStatus === "success"

  return {
    success: isSuccess,
    loginUrl: safeString(response.redirect_url),
    token: safeString(response.token),
    message: safeString(response.message),
  }
}

/**
 * Normalize user fields response
 * Used for getting form field definitions for user create/edit
 *
 * @param {Object|Array} response - Raw user fields response
 * @returns {Object} Normalized response
 * @returns {Array} returns.fields - Array of field definitions
 */
export function normalizeUserFieldsResponse(response) {
  if (!response) {
    return {
      fields: [],
    }
  }

  // Handle array response directly
  if (Array.isArray(response)) {
    return {
      fields: response.map(normalizeUserField),
    }
  }

  // Handle object with fields property
  if (response.fields && Array.isArray(response.fields)) {
    return {
      fields: response.fields.map(normalizeUserField),
    }
  }

  return {
    fields: [],
  }
}

/**
 * Normalize single user field definition
 *
 * @param {Object} field - Raw field object
 * @returns {Object} Normalized field
 */
function normalizeUserField(field) {
  if (!field || typeof field !== "object") {
    return {}
  }

  return {
    id: safeNumber(field.id),
    name: safeString(field.name),
    type: safeString(field.type),
    label: safeString(field.label),
    required: field.required === 1 || field.required === true,
    showInList: field.show_in_list === 1 || field.show_in_list === true,
    searchable: field.searchable === 1 || field.searchable === true,
    default: field.default !== undefined ? field.default : null,
    options: field.options !== undefined ? field.options : null,
    unload: field.unload === 1 || field.unload === true,
  }
}
